import React, { useCallback, useEffect } from 'react';

import { Flex } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import toggleicon from '../../images/arrow-left-arrow-right.svg';

import {
    fetchServices,
    selectCalendarDateCompareOperator,
    selectCalendarIntervalMonths,
    selectCalendarViewActualStartDate,
    selectCalendarViewStartDate,
    selectCalendarIsDateMode,
    selectCalendarStoredDateModeValues,
    selectCalendarStoredMonthYearValues,
    selectDailyDateCompareOperator,
    selectDailyIntervalMonths,
    selectDailyViewActualStartDate,
    selectDailyViewStartDate,
    selectDailyIsDateMode,
    selectDailyStoredDateModeValues,
    selectDailyStoredMonthYearValues,
    selectIsServicePopupVisible,
    selectIsStatusPopupVisible,
    setPopupVisibility,
    updateCalendarView,
    updateDailyView,
    toggleViewMode,
    updateStoredDateModeValues,
    updateStoredMonthYearValues
} from '../../store/serviceCalendarSlice';
import dayjs from 'dayjs';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

const TopSection = ({ page }) => {
    const dispatch = useDispatch();

    // --- Select state from Redux based on page ---
    // Use selectors conditionally based on page prop
    const viewActualStartDate = useSelector(page === 'calendar' ? selectCalendarViewActualStartDate : selectDailyViewActualStartDate);
    const viewStartDate = useSelector(page === 'calendar' ? selectCalendarViewStartDate : selectDailyViewStartDate);
    const intervalMonths = useSelector(page === 'calendar' ? selectCalendarIntervalMonths : selectDailyIntervalMonths);
    const currentOperator = useSelector(page === 'calendar' ? selectCalendarDateCompareOperator : selectDailyDateCompareOperator);
    const isStatusPopupVisible = useSelector(selectIsStatusPopupVisible);
    const isServicePopupVisible = useSelector(selectIsServicePopupVisible);

    // Get view mode states from Redux instead of local state
    const isDateMode = useSelector(page === 'calendar' ? selectCalendarIsDateMode : selectDailyIsDateMode);
    const storedDateModeValues = useSelector(page === 'calendar' ? selectCalendarStoredDateModeValues : selectDailyStoredDateModeValues);
    const storedMonthYearValues = useSelector(page === 'calendar' ? selectCalendarStoredMonthYearValues : selectDailyStoredMonthYearValues);
    // --- End Select state from Redux ---

    // --- Constants for Month/Year selectors ---
    const currentMonth = new Date().getMonth() + 1; // For default selection if needed
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    const minYear = years[0];
    const maxYear = years[years.length - 1];
    // --- End Constants ---

    // Update stored values when primary values change
    useEffect(() => {
        if (isDateMode) {
            dispatch(
                updateStoredDateModeValues({
                    page,
                    values: {
                        date: viewActualStartDate,
                        compare: currentOperator
                    }
                })
            );
        }
    }, [viewActualStartDate, currentOperator, isDateMode, dispatch, page]);

    useEffect(() => {
        if (!isDateMode) {
            const currentMoment = dayjs(viewStartDate);
            dispatch(
                updateStoredMonthYearValues({
                    page,
                    values: {
                        month: currentMoment.month() + 1,
                        year: currentMoment.year()
                    }
                })
            );
        }
    }, [viewStartDate, isDateMode, dispatch, page]);

    // Combined handler for date/operator changes in Date Mode
    const handleDateOrOperatorChange = useCallback(
        (newDateISO, newOperator) => {
            const date = newDateISO || viewActualStartDate; // Use current if only operator changed
            const operator = newOperator || currentOperator; // Use current if only date changed

            let payloadStartDate = date;
            let payloadActualStartDate = date;

            if (operator === 'bigger') {
                payloadStartDate = dayjs(date).toISOString();
            } else if (operator === 'smaller') {
                payloadActualStartDate = date;
                // Different behavior for calendar vs daily
                if (page === 'calendar') {
                    payloadStartDate = dayjs(date).subtract(intervalMonths, 'month').toISOString(); // Calendar: subtract months
                } else {
                    payloadStartDate = date; // Daily: maintain the same date
                }
            } else {
                // equals
                payloadStartDate = date; // Both start and actual are the same
            }

            const updateAction = page === 'calendar' ? updateCalendarView : updateDailyView;

            dispatch(
                updateAction({
                    startDate: payloadStartDate,
                    actualStartDate: payloadActualStartDate,
                    operator: operator
                    // intervalMonths remains unchanged unless explicitly set
                })
            );
            dispatch(fetchServices());
        },
        [dispatch, viewActualStartDate, currentOperator, intervalMonths, page]
    );

    const handleCompareChange = (e) => {
        handleDateOrOperatorChange(null, e.target.value);
    };

    const handleDateChange = (e) => {
        handleDateOrOperatorChange(dayjs(e.target.value).toISOString(), null);
    };

    const handleDatePrevious = useCallback(() => {
        const newDate = dayjs(viewActualStartDate).subtract(1, 'day').toISOString();
        handleDateOrOperatorChange(newDate, null);
    }, [viewActualStartDate, handleDateOrOperatorChange]);

    const handleDateNext = useCallback(() => {
        const newDate = dayjs(viewActualStartDate).add(1, 'day').toISOString();
        handleDateOrOperatorChange(newDate, null);
    }, [viewActualStartDate, handleDateOrOperatorChange]);

    // Handlers for Month/Year Mode
    const updateMonthYearView = useCallback(
        (month, year, day) => {
            const targetMonth = month || storedMonthYearValues.month;
            const targetYear = year || storedMonthYearValues.year;
            const targetDay = day || storedMonthYearValues.day || dayjs(viewActualStartDate).date();
            const daysInTargetMonth = dayjs()
                .year(targetYear)
                .month(targetMonth - 1)
                .daysInMonth();
            const safeDay = Math.min(targetDay, daysInTargetMonth);
            const newStartDate = dayjs()
                .year(targetYear)
                .month(targetMonth - 1)
                .date(safeDay)
                .toISOString();

            const updateAction = page === 'calendar' ? updateCalendarView : updateDailyView;

            dispatch(
                updateAction({
                    startDate: newStartDate,
                    actualStartDate: newStartDate,
                    operator: 'bigger'
                })
            );
            dispatch(fetchServices());
        },
        [dispatch, storedMonthYearValues.month, storedMonthYearValues.year, storedMonthYearValues.day, viewActualStartDate, page]
    );

    const handleSelectedMonth = (e) => {
        const newMonth = Number(e.target.value);
        const day = storedMonthYearValues.day || dayjs(viewActualStartDate).date();
        dispatch(
            updateStoredMonthYearValues({
                page,
                values: { month: newMonth, day }
            })
        );
        updateMonthYearView(newMonth, null, day);
    };

    const handleSelectedYear = (e) => {
        const newYear = Number(e.target.value);
        const day = storedMonthYearValues.day || dayjs(viewActualStartDate).date();
        dispatch(
            updateStoredMonthYearValues({
                page,
                values: { year: newYear, day }
            })
        );
        updateMonthYearView(null, newYear, day);
    };

    const handleMonthPrevious = useCallback(() => {
        let newMonth = storedMonthYearValues.month - 1;
        let newYear = storedMonthYearValues.year;
        let day = storedMonthYearValues.day || dayjs(viewActualStartDate).date();
        if (newMonth < 1) {
            newMonth = 12;
            newYear--;
        }
        dispatch(
            updateStoredMonthYearValues({
                page,
                values: { month: newMonth, year: newYear, day }
            })
        );
        updateMonthYearView(newMonth, newYear, day);
    }, [storedMonthYearValues, updateMonthYearView, dispatch, page, viewActualStartDate]);

    const handleMonthNext = useCallback(() => {
        let newMonth = storedMonthYearValues.month + 1;
        let newYear = storedMonthYearValues.year;
        let day = storedMonthYearValues.day || dayjs(viewActualStartDate).date();
        if (newMonth > 12) {
            newMonth = 1;
            newYear++;
        }
        dispatch(
            updateStoredMonthYearValues({
                page,
                values: { month: newMonth, year: newYear, day }
            })
        );
        updateMonthYearView(newMonth, newYear, day);
    }, [storedMonthYearValues, updateMonthYearView, dispatch, page, viewActualStartDate]);

    const handleYearPrevious = useCallback(() => {
        const newYear = storedMonthYearValues.year - 1;
        const day = storedMonthYearValues.day || dayjs(viewActualStartDate).date();
        if (newYear >= minYear) {
            dispatch(
                updateStoredMonthYearValues({
                    page,
                    values: { year: newYear, day }
                })
            );
            updateMonthYearView(null, newYear, day);
        }
    }, [storedMonthYearValues.year, storedMonthYearValues.day, minYear, updateMonthYearView, dispatch, page, viewActualStartDate]);

    const handleYearNext = useCallback(() => {
        const newYear = storedMonthYearValues.year + 1;
        const day = storedMonthYearValues.day || dayjs(viewActualStartDate).date();
        if (newYear <= maxYear) {
            dispatch(
                updateStoredMonthYearValues({
                    page,
                    values: { year: newYear, day }
                })
            );
            updateMonthYearView(null, newYear, day);
        }
    }, [storedMonthYearValues.year, storedMonthYearValues.day, maxYear, updateMonthYearView, dispatch, page, viewActualStartDate]);

    // Handler for Interval Months change
    const handleSelectedMonths = (e) => {
        const months = parseInt(e.target.value, 10);
        const updateAction = page === 'calendar' ? updateCalendarView : updateDailyView;

        dispatch(updateAction({ intervalMonths: months }));
        dispatch(fetchServices());
    };

    const handleToggle = () => {
        const nextIsDateMode = !isDateMode;
        dispatch(toggleViewMode({ page, isDateMode: nextIsDateMode }));

        if (nextIsDateMode) {
            // Switching TO date mode - use stored month/year/day if available, otherwise sync with current view
            let syncedDate;
            if (storedMonthYearValues && storedMonthYearValues.month && storedMonthYearValues.year) {
                const day = storedMonthYearValues.day || dayjs(viewActualStartDate).date();
                const daysInMonth = dayjs()
                    .year(storedMonthYearValues.year)
                    .month(storedMonthYearValues.month - 1)
                    .daysInMonth();
                const safeDay = Math.min(day, daysInMonth);
                syncedDate = dayjs()
                    .year(storedMonthYearValues.year)
                    .month(storedMonthYearValues.month - 1)
                    .date(safeDay)
                    .toISOString();
            } else {
                syncedDate = dayjs(viewStartDate).toISOString();
            }

            dispatch(
                updateStoredDateModeValues({
                    page,
                    values: {
                        date: syncedDate,
                        compare: storedDateModeValues.compare || 'equals'
                    }
                })
            );

            handleDateOrOperatorChange(syncedDate, storedDateModeValues.compare || 'equals');
        } else {
            // Switching TO month/year mode - sync with current date but preserve the specific date in storage
            const currentMoment = dayjs(viewActualStartDate);
            const syncedMonth = currentMoment.month() + 1;
            const syncedYear = currentMoment.year();
            const syncedDay = currentMoment.date();

            dispatch(
                updateStoredMonthYearValues({
                    page,
                    values: { month: syncedMonth, year: syncedYear, day: syncedDay }
                })
            );

            dispatch(
                updateStoredDateModeValues({
                    page,
                    values: {
                        date: viewActualStartDate,
                        compare: currentOperator
                    }
                })
            );

            const newStartDate = dayjs()
                .year(syncedYear)
                .month(syncedMonth - 1)
                .date(syncedDay)
                .toISOString();
            const updateAction = page === 'calendar' ? updateCalendarView : updateDailyView;
            dispatch(
                updateAction({
                    startDate: newStartDate,
                    actualStartDate: newStartDate,
                    operator: 'bigger'
                })
            );
            dispatch(fetchServices());
        }
    };

    // --- End Handlers ---
    const formattedDateForInput = dayjs(viewStartDate).format('YYYY-MM-DD');
    const displayMonth = !isDateMode ? storedMonthYearValues.month : dayjs(viewStartDate).month() + 1;
    const displayYear = !isDateMode ? storedMonthYearValues.year : dayjs(viewStartDate).year();
    // ---

    return (
        <div className="space search_font scheduled-services-outer flex">
            <button title="Switch Date/Month Filter" onClick={handleToggle} className="next-previous toggle-btn">
                <img src={toggleicon} alt="icon" width="18" height="18" />
            </button>

            {/* Month/Year Selection Mode */}
            {!isDateMode && (
                <>
                    <div className="search_font month-group">
                        Month
                        <select
                            value={displayMonth} // Read from state
                            onChange={handleSelectedMonth} // Dispatch action
                            className="select-months search_font border border-gray-700"
                            style={{ height: '25px' }}>
                            {/* Month options */}
                            <option value="1">January</option>
                            <option value="2">February</option>
                            <option value="3">March</option>
                            <option value="4">April</option>
                            <option value="5">May</option>
                            <option value="6">June</option>
                            <option value="7">July</option>
                            <option value="8">August</option>
                            <option value="9">September</option>
                            <option value="10">October</option>
                            <option value="11">November</option>
                            <option value="12">December</option>
                        </select>
                        <button
                            title="Previous Month"
                            onClick={handleMonthPrevious}
                            className={`next-previous prev-btn ${displayYear === minYear && displayMonth === 1 ? 'next-previous-disable' : ''}`}
                            disabled={displayYear === minYear && displayMonth === 1}>
                            <LeftOutlined
                                style={{
                                    strokeWidth: '30',
                                    stroke: 'white'
                                }}
                            />
                        </button>
                        <button
                            title="Next Month"
                            onClick={handleMonthNext}
                            className={`next-previous next-btn font-bold ${displayYear === maxYear && displayMonth === 12 ? 'next-previous-disable' : ''}`}
                            disabled={displayYear === maxYear && displayMonth === 12}>
                            <RightOutlined
                                style={{
                                    strokeWidth: '30',
                                    stroke: 'white'
                                }}
                            />
                        </button>
                    </div>
                    <div className="search_font year-group">
                        <span>Year</span>
                        <select
                            value={displayYear} // Read from state
                            onChange={handleSelectedYear} // Dispatch action
                            className="select-months search_font border border-gray-700"
                            style={{ height: '25px' }}>
                            {years.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                        <button
                            title="Previous Year"
                            onClick={handleYearPrevious}
                            className={`next-previous prev-btn ${displayYear <= minYear ? 'next-previous-disable' : ''}`}
                            disabled={displayYear <= minYear}>
                            <LeftOutlined
                                style={{
                                    strokeWidth: '30',
                                    stroke: 'white'
                                }}
                            />
                        </button>
                        <button
                            title="Next Year"
                            onClick={handleYearNext}
                            className={`next-previous next-btn ${displayYear >= maxYear ? 'next-previous-disable' : ''}`}
                            disabled={displayYear >= maxYear}>
                            <RightOutlined
                                style={{
                                    strokeWidth: '30',
                                    stroke: 'white'
                                }}
                            />
                        </button>
                    </div>
                </>
            )}

            {/* Date +/- Selection Mode */}
            {isDateMode && (
                <div className={`search_font date-group ${page === 'daily' ? 'mr-[20px]' : ''}`}>
                    Date
                    {page === 'daily' && (
                        <select
                            value={currentOperator} // Read from Redux state
                            onChange={handleCompareChange} // Dispatch action
                            className="select-compare-sign border border-gray-700">
                            <option value="equals">&#61;</option>
                            <option value="bigger">&ge;</option>
                            <option value="smaller">&le;</option>
                        </select>
                    )}
                    <input
                        type="date"
                        value={formattedDateForInput} // Read formatted date from Redux state
                        onChange={handleDateChange} // Dispatch action
                        className="search_font border border-gray-700"
                        style={{ width: '8em', height: '25px' }}
                    />
                    <button
                        title="Previous Day"
                        onClick={handleDatePrevious} // Dispatch action
                        className="next-previous prev-btn">
                        <LeftOutlined
                            style={{
                                strokeWidth: '30',
                                stroke: 'white'
                            }}
                        />
                    </button>
                    <button
                        title="Next Day"
                        onClick={handleDateNext} // Dispatch action
                        className="next-previous next-btn">
                        <RightOutlined
                            style={{
                                strokeWidth: '30',
                                stroke: 'white'
                            }}
                        />
                    </button>
                </div>
            )}

            {/* Show Months control - Only for Calendar page */}
            {page === 'calendar' && (
                <div className="show-group">
                    Show
                    <select
                        value={intervalMonths}
                        onChange={handleSelectedMonths}
                        className="select-months search_font border border-gray-700"
                        style={{ height: '25px' }}>
                        {/* Month interval options */}
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="6">6</option>
                        <option value="9">9</option>
                        <option value="12">12</option>
                        <option value="18">18</option>
                        <option value="24">24</option>
                        <option value="36">36</option>
                    </select>
                    Months
                </div>
            )}

            <div className="schedule-btn-actions">
                <button
                    onClick={() => dispatch(setPopupVisibility({ popupName: 'filter', isVisible: true }))} // Dispatch action
                    className="btn-filter"
                    // ref={showFilterButtonRef} // Keep ref only if needed for positioning
                >
                    Filter
                </button>
                <Link to={`/scheduled-services/add?from=${page}`}>
                    <button className="btn-filter">+Add Scheduled</button>
                </Link>
                <Link to={`/unscheduled-services/add?from=${page}`}>
                    <button className="btn-filter">+Add Unscheduled</button>
                </Link>
                <Link to={`/defect-tickets/add?from=${page}`}>
                    <button className="btn-filter">+Add Defect</button>
                </Link>
            </div>

            <Flex gap="small" className="ml-2">
                <button
                    onClick={() =>
                        dispatch(
                            setPopupVisibility({
                                popupName: 'service',
                                isVisible: !isServicePopupVisible
                            })
                        )
                    } // Dispatch action
                    className="service-status">
                    Service
                </button>
                <button
                    onClick={() =>
                        dispatch(
                            setPopupVisibility({
                                popupName: 'status',
                                isVisible: !isStatusPopupVisible
                            })
                        )
                    } // Dispatch action
                    className="service-status">
                    Status
                </button>
            </Flex>
        </div>
    );
};

export default TopSection;

TopSection.propTypes = {
    page: PropTypes.string.isRequired
};
